import React from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

// Comparedly components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * AssistantMenu Component
 * Renders the popup menu for the Chart Assistant.
 */
function AssistantMenu({
    menu,
    closeMenu,
    usage,
    entitlements,
    response,
    loading,
    error,
    query,
    setQuery,
    handleKeyDown,
    handleAnalyze
}) {
    return (
        <Menu
            id="chart-assistant-menu"
            anchorEl={menu}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            open={Boolean(menu)}
            onClose={closeMenu}
            disableScrollLock={true} // ✅ Prevent scroll lock to keep sticky headers visible
            PaperProps={{
                sx: {
                    mt: 1,
                    width: 350,
                    maxHeight: 500,
                    p: 2,
                    borderRadius: "12px",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.18)",
                },
            }}
        >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6" fontWeight="medium" color="text">
                    ✨ Comparedly Lens
                </MDTypography>
                <Icon sx={{ cursor: "pointer", fontSize: "1.2rem" }} onClick={closeMenu}>
                    close
                </Icon>
            </MDBox>

            {/* usage badge - Only show if entitled */}
            {usage && entitlements?.features?.includes("AI Powered Insight") && (
                <MDBox display="flex" flexDirection="column" alignItems="flex-end" mb={1}>
                    <MDBox
                        sx={{
                            backgroundColor: usage.remaining > 3 ? "#4CAF50" : usage.remaining > 0 ? "#FF9800" : "#F44336",
                            borderRadius: "12px",
                            px: 1,
                            py: 0.25,
                            display: "flex",
                            alignItems: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                    >
                        <Icon sx={{ color: "#fff", fontSize: "10px !important", mr: 0.5 }}>bolt</Icon>
                        <MDTypography variant="caption" fontWeight="bold" color="white" sx={{ fontSize: "10px" }}>
                            {usage.remaining}/{usage.limit} Left
                        </MDTypography>
                    </MDBox>

                    {/* Subtle Reset Text - Only when exhausted */}
                    {usage.remaining === 0 && (
                        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem", opacity: 0.6, mt: 0.5 }}>
                            Resets at midnight UTC
                        </MDTypography>
                    )}
                </MDBox>
            )}

            {/* Response Area */}
            <MDBox
                mb={2}
                sx={{
                    minHeight: "100px",
                    maxHeight: "250px",
                    overflowY: "auto",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    p: 2,
                }}
            >
                {!response && !loading && !error && (
                    <MDTypography variant="caption" color="text" fontWeight="light">
                        Ask me anything about this chart! <br />
                        Example: "Why is there a spike in August?"
                    </MDTypography>
                )}

                {loading && (
                    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress size={24} color="info" />
                        <MDTypography variant="caption" color="text" mt={1}>
                            Analysing data...
                        </MDTypography>
                    </MDBox>
                )}

                {error && (
                    <MDTypography variant="caption" color="error">
                        {error}
                    </MDTypography>
                )}

                {response && (
                    // Use standard Typography (or Markdown renderer if available) for the response
                    <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#344767", whiteSpace: "pre-wrap" }}>
                        {response}
                    </Typography>
                )}
            </MDBox>

            <Divider />

            <MDBox display="flex" alignItems="center" mt={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Ask a question..."
                    size="small"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            paddingRight: "40px",
                        },
                    }}
                />
                <IconButton
                    size="small"
                    color="info"
                    onClick={handleAnalyze}
                    disabled={loading || !query.trim() || (usage && usage.remaining <= 0)}
                    sx={{
                        position: "absolute",
                        right: 24, // aligned inside the input
                        backgroundColor: "transparent",
                    }}
                >
                    <Icon>send</Icon>
                </IconButton>
            </MDBox>

            <MDBox mt={1} textAlign="center">
                <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
                    AI can make mistakes. Please double check results.
                </MDTypography>
            </MDBox>
        </Menu>
    );
}

AssistantMenu.propTypes = {
    menu: PropTypes.any,
    closeMenu: PropTypes.func.isRequired,
    usage: PropTypes.object,
    entitlements: PropTypes.object,
    response: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    query: PropTypes.string.isRequired,
    setQuery: PropTypes.func.isRequired,
    handleKeyDown: PropTypes.func.isRequired,
    handleAnalyze: PropTypes.func.isRequired
};

export default AssistantMenu;
